import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Collections } from '@graylog/enterprise-api';

import UserNotification from 'util/UserNotification';
import type { Collection, CollectionDetail } from 'collections/types';
import { COLLECTIONS_QUERY_KEY } from 'collections/hooks/useCollections';
import type { EntityShare } from 'actions/permissions/EntityShareActions';

const deleteCollection = async ({ id }: { id: Collection['id'] }) => Collections.remove(id);
const bulkDeleteCollections = async ({ entityIds }: { entityIds: Array<Collection['id']> }) =>
  Collections.bulkDelete({ entity_ids: entityIds });

type CollectionRequest = CollectionDetail & EntityShare;
const createCollection = async (collectionRequest: CollectionRequest) => {
  const { share_request, ...rest } = collectionRequest;

  return Collections.create({
    entity: {
      title: rest?.title,
      description: rest?.description,
      id: rest?.id,
      entities: rest?.entities,
    },
    share_request: {
      selected_grantee_capabilities: share_request?.selected_grantee_capabilities?.toJS(),
      selected_collections: share_request?.selected_collections,
    },
  });
};

const updateCollection = async (newCollection: Collection) =>
  Collections.update(newCollection.id, {
    title: newCollection?.title,
    description: newCollection?.description,
  });

const updateCollectionsForEntity = async ({
  entityId,
  payload,
}: {
  entityId: string;
  payload: {
    addTo: Array<string>;
    removeFrom: Array<string>;
  };
}) => Collections.updateCollections(entityId, { add_to: payload.addTo, remove_from: payload.removeFrom });

const useCollectionMutation = () => {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: deleteCollection,

    onError: (errorThrown) => {
      UserNotification.error(`Deleting Collection failed with status: ${errorThrown}`, 'Could not delete Collection');
    },

    onSuccess: () => {
      UserNotification.success('Collection has been successfully deleted.', 'Success!');

      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: 'active' });
    },
  });

  const bulkRemove = useMutation({
    mutationFn: bulkDeleteCollections,
  });
  const updateEntityCollectionsMutation = useMutation({
    mutationFn: updateCollectionsForEntity,

    onError: (errorThrown) => {
      UserNotification.error(
        `Updating entity collection(s) failed with status: ${errorThrown}`,
        'Could not update entity collections',
      );
    },

    onSuccess: () => {
      UserNotification.success('Entity collection(s) has been successfully updated.', 'Success!');
      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY, 'entity'], type: 'active' });
    },
  });

  const create = useMutation({
    mutationFn: createCollection,

    onError: (errorThrown) => {
      UserNotification.error(`Creating Collection failed with status: ${errorThrown}`, 'Could not create Collection');
    },

    onSuccess: () => {
      UserNotification.success('Collection has been successfully created.', 'Success!');

      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: 'active' });
    },
  });

  const update = useMutation({
    mutationFn: updateCollection,

    onError: (errorThrown) => {
      UserNotification.error(`Updating Collection failed with status: ${errorThrown}`, 'Could not update Collection');
    },

    onSuccess: () => {
      UserNotification.success('Collection has been successfully updated.', 'Success!');

      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: 'active' });
    },
  });

  return {
    deleteCollection: remove.mutateAsync,
    bulkDeleteCollectionsMutation: bulkRemove,
    createCollection: create.mutateAsync,
    updateCollection: update.mutateAsync,
    updateEntityCollectionsMutation: updateEntityCollectionsMutation.mutateAsync,
  };
};

export default useCollectionMutation;
